import { useEffect, useMemo, useState } from "react";
import { Button, Input } from "../../ui";
import { formatSar, hasValidSarPrecision, sarToMinorUnits } from "../../../utils/billingMoney";
import { newIdempotencyKey, previewAiWalletTopUpAPI, requestAiWalletTopUpAPI } from "../../../utils/api/aiWallet";
import { fill } from "./aiWalletText";

const QUICK_AMOUNTS = [20, 50, 100, 200, 500];

// "Add funds": asks for an amount, shows what will be invoiced (credit + VAT,
// computed by the server) and creates the top-up invoice. Paying it is the
// normal receipt upload; the balance is added after Finance approves.
const AiWalletTopUpModal = ({ storeId, policy, lang, text, onCancel, onDone }) => {
  const isRtl = lang !== "en";
  const minMinor = policy?.minTopUpMinor ?? 2000;
  const maxMinor = policy?.maxTopUpMinor ?? 500000;
  const [amount, setAmount] = useState((minMinor / 100).toFixed(2));
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // One key per opened form: a double click cannot create two invoices.
  const idempotencyKey = useMemo(() => newIdempotencyKey("topup"), []);

  const problem = useMemo(() => {
    if (!hasValidSarPrecision(amount)) return text.invalidAmount;
    const minor = sarToMinorUnits(amount);
    if (minor < minMinor) return fill(text.belowMinimum, { amount: formatSar(minMinor, lang) });
    if (minor > maxMinor) return fill(text.aboveMaximum, { amount: formatSar(maxMinor, lang) });
    return "";
  }, [amount, minMinor, maxMinor, lang, text]);

  useEffect(() => {
    if (problem) {
      setQuote(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await previewAiWalletTopUpAPI(storeId, amount);
        if (!cancelled) setQuote(result);
      } catch (err) {
        if (!cancelled) setQuote(null);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [amount, problem, storeId]);

  const submit = async () => {
    if (submitting || problem) return;
    setSubmitting(true);
    setError("");
    try {
      const result = await requestAiWalletTopUpAPI(storeId, amount, idempotencyKey);
      await onDone(result);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="billing-modal-backdrop" dir={isRtl ? "rtl" : "ltr"}>
      <div className="billing-modal aiw-modal" role="dialog" aria-modal="true" aria-label={text.topUpModalTitle}>
        <h3>{text.topUpModalTitle}</h3>
        <p>{text.topUpModalNote}</p>
        {error && <div className="billing-error">{error}</div>}

        <div className="aiw-quick-amounts">
          {QUICK_AMOUNTS.filter((value) => value * 100 >= minMinor && value * 100 <= maxMinor).map((value) => (
            <button key={value} type="button" className={`aiw-chip ${Number(amount) === value ? "active" : ""}`} onClick={() => setAmount(value.toFixed(2))}>
              {formatSar(value * 100, lang)}
            </button>
          ))}
        </div>
        <Input label={text.amountLabel} inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value.trim())} error={problem || undefined} helperText={fill(text.minTopUp, { amount: formatSar(minMinor, lang) })} />

        <div className="billing-preview-box">
          <div>
            <span>{text.credit}</span>
            <strong>{quote ? formatSar(quote.creditMinor, lang) : "-"}</strong>
          </div>
          <div>
            <span>{fill(text.vat, { rate: quote ? quote.vatRateBps / 100 : (policy?.vatRateBps ?? 0) / 100 })}</span>
            <strong>{quote ? formatSar(quote.vatAmountMinor, lang) : "-"}</strong>
          </div>
          <div>
            <span>{text.totalToPay}</span>
            <strong>{quote ? formatSar(quote.payableMinor, lang) : "-"}</strong>
          </div>
        </div>

        <div className="billing-modal-actions">
          <Button appearance="filled" loading={submitting} disabled={submitting || Boolean(problem)} onClick={submit}>
            {submitting ? text.creating : text.createRequest}
          </Button>
          <Button appearance="ghost" disabled={submitting} onClick={onCancel}>
            {text.cancel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AiWalletTopUpModal;
